import { useEffect, useState } from 'react';
import { useAuth, ShellAuthProvider } from '@aimsos/auth';
import { UnifiedProfilesApp } from '@aimsos/unified-profiles';
import { registerNamespace, i18n } from '../i18n/bootstrap.js';

/**
 * Mounts the Unified Profiles application inside the Core Shell.
 * Mirrors the same embedding pattern as DashboardsEmbed / WorkspacesEmbed.
 *
 * Reads from the unified useAuth() (backed by our own backend JWT) instead of
 * useAuth0() directly, so this still mounts correctly on a
 * {slug}.{rootDomain} subdomain landed on via a cross-subdomain handoff, where
 * the Auth0 SDK itself has no session.
 */
const UnifiedProfilesEmbed = () => {
  const { user, token, isAuthenticated, getAccessTokenSilently } = useAuth();
  const [i18nReady, setI18nReady] = useState(false);

  useEffect(() => {
    let cancelled = false;
    import('@aimsos/unified-profiles/i18n/register')
      .then(({ registerUnifiedProfilesI18n }) => {
        registerUnifiedProfilesI18n(registerNamespace);
        return i18n.loadNamespaces('unified-profiles');
      })
      .then(() => {
        if (!cancelled) setI18nReady(true);
      })
      .catch((err) => console.error('[UnifiedProfilesEmbed] i18n:', err));
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!isAuthenticated) return;
    window.__AIMS_SHELL__ = { mounted: true };
    return () => {
      delete window.__AIMS_SHELL__;
    };
  }, [isAuthenticated]);

  if (!isAuthenticated || !token || !i18nReady) return null;

  return (
    <ShellAuthProvider
      token={token}
      user={user}
      isAuthenticated={isAuthenticated}
      getAccessTokenSilently={getAccessTokenSilently}
    >
      <UnifiedProfilesApp />
    </ShellAuthProvider>
  );
};

export default UnifiedProfilesEmbed;
